/**
 * Module dependencies.
 */

import { ContactProps, ContactsPageProps } from 'src/types/contacts';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp } from 'styled-tools';
import CommercialContacts from 'src/components/contacts/commercial-contacts';
import ContactsSection from 'src/components/contacts/contacts-section';
import KioskModulesBuilder from 'src/components/kiosk/modules/modules-builder';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import ModulesBuilder from 'src/components/modules/modules-builder';
import React, { ReactElement, useCallback, useRef } from 'react';
import ShowroomsSection from 'src/components/sections/showrooms-section';
import styled, { css } from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';

/**
 * Export `Props` type.
 */

export type Props = {
  contacts: ContactProps[],
  data: ContactsPageProps,
  isKiosk?: boolean
  slug: string
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isLoading: boolean }>`
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}
`;

/**
 * `ContactsPage` component.
 */

const ContactsPage = (props: Props): ReactElement => {
  const {
    data,
    isLoading,
    isSuccess
  } = usePage<ContactsPageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const contactFormRef = useRef<HTMLDivElement>();
  const handleGotoContactFormSection = useCallback(() => {
    if (contactFormRef.current) {
      const formElement = contactFormRef.current;
      const offset = props.isKiosk ? 0 : 140;
      const yPosition = formElement.getBoundingClientRect().top + window.pageYOffset - offset;

      window.scrollTo({
        behavior: 'smooth',
        top: yPosition
      });
    }
  }, [props.isKiosk]);

  const title = data?.title;
  const description = data?.description;

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={data?.descriptionSeo || description}
        hrefLangs={getHrefLangs(data?.translations, 'contacts')}
        isKiosk={props?.isKiosk}
        pageTitle={title}
        title={data?.titleSeo}
      />

      <Wrapper isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <>
            <ContactsSection
              contacts={props?.contacts}
              description={description}
              hasPageTitle
              isKiosk={props?.isKiosk}
              onGotoContactFormSection={handleGotoContactFormSection}
              subtitle={data?.subtitle}
              title={title}
            />

            <CommercialContacts isKiosk={props?.isKiosk} />

            <ShowroomsSection isKiosk={props?.isKiosk} />

            <div ref={contactFormRef}>
              {props?.isKiosk ? (
                <KioskModulesBuilder
                  allowed={['contactsForm']}
                  // @ts-ignore
                  modules={data?.modules}
                />
              ) : (
                <ModulesBuilder
                  allowed={['contactsForm']}
                  modules={data?.modules}
                />
              )}
            </div>

          </>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `ContactsPage` component.
 */

export default ContactsPage;
